/*
  Second Bloom — fast add to cart
  ------------------------------------------------------------------
  Before: pressing Add to cart waited for Shopify to add the piece AND
  render the drawer (~0.7s on a good connection, 1–2s+ on mobile data)
  before anything visible happened.

  Now:
  1. The drawer slides open the moment Add to cart is pressed, with the
     piece already shown as "Adding to your bag…". When Shopify answers,
     the drawer is swapped for the real cart, exactly as before.
     If the add fails (e.g. someone else just bought the piece), the
     drawer closes again and the theme's normal error shows on the page.
  2. Shopify is only asked to render the cart sections that exist on the
     current page (the full cart-page section is no longer rendered on
     every product page).
  3. The theme no longer builds a new Cart() three times per add.
  4. The two background pollers that hit /cart.js every 4s (bundle box)
     and every 5s (header badge) are stopped. Both still refresh after
     every cart change and when the shopper comes back to the tab.
  5. Removing a piece in the drawer fades it out immediately, and a failed
     removal resyncs the drawer instead of leaving it stuck.
  6. Proceed to checkout reacts on tap ("Opening checkout" + spinner) while
     Shopify redirects, ignores accidental double taps, and resets itself
     (with a fresh cart) if the shopper comes back with the Back button.
     The checkout request itself is unchanged.
  7. The whole drawer appears at once on Add to cart: piece count, bundle
     bar and message, assurances and checkout button are drawn straight
     away (from a template in snippets/cart-drawer.liquid when the bag was
     empty). Only the checkout total and "You saved" shimmer until Shopify
     confirms them, since Shopify calculates the bundle discount.

  Rollback: remove the sb-fast-cart.js line from layout/theme.liquid.
*/
(function () {
  'use strict';
  if (window.SBFastCart) return;

  var SAFETY_MS = 15000;
  var safetyTimer = null;
  var restoreHTML = null;

  function drawer() {
    return document.getElementById('Cart-Drawer');
  }

  function overlayIsOpen(panel) {
    return !!(panel && window.OverlayManager && OverlayManager.isOpen(panel));
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function textOf(node) {
    return node ? node.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  /* ---------- styles ---------- */

  function injectStyles() {
    if (document.getElementById('sb-fast-cart-css')) return;
    var css = [
      '#Cart-Drawer.sb-adding .side-panel-header{position:relative}',
      '#Cart-Drawer.sb-adding .side-panel-header::after{content:"";position:absolute;left:0;right:0;bottom:0;height:2px;',
      'background:linear-gradient(90deg,rgba(145,157,203,0),#919dcb,rgba(145,157,203,0));background-size:40% 100%;',
      'background-repeat:no-repeat;animation:sbFastBar 0.9s linear infinite}',
      '@keyframes sbFastBar{from{background-position:-40% 0}to{background-position:140% 0}}',
      /* While adding, the footer is fully drawn but not clickable; only the
         amounts Shopify still has to confirm shimmer. */
      '#Cart-Drawer.sb-adding .side-panel-footer{pointer-events:none}',
      '#Cart-Drawer.sb-adding .checkout-button{opacity:.85}',
      '.cart-drawer .sb-skel{color:transparent!important;border-radius:4px;',
      'background:linear-gradient(90deg,#eceef3 25%,#f6f7fa 50%,#eceef3 75%);background-size:200% 100%;',
      'animation:sbShimmer 1.1s linear infinite}',
      '.cart-drawer .checkout-button .sb-skel{display:inline-block;min-width:72px;height:1.1em;',
      'background:linear-gradient(90deg,rgba(255,255,255,.16) 25%,rgba(255,255,255,.34) 50%,rgba(255,255,255,.16) 75%);',
      'background-size:200% 100%}',
      '.cart-drawer .sb-cart-saved.sb-skel{display:block;width:150px;height:16px;margin:0 auto 9px}',
      '.cart-drawer .sb-tiers__lead .sb-skel{display:inline-block;width:85%;height:14px;vertical-align:middle}',
      '@keyframes sbShimmer{from{background-position:200% 0}to{background-position:-200% 0}}',
      '.cart-drawer .sb-pending-item{animation:sbFastIn .25s ease-out both}',
      '@keyframes sbFastIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}',
      '.cart-drawer .sb-pending-item .sb-pending-note{grid-area:meta;font-size:11.5px;line-height:1.3;color:#8b9099}',
      '.cart-drawer .sb-pending-ph{display:block;width:82px;height:110px;background:#f2f3f6;border-radius:2px}',
      /* Items can't be removed mid-add (the list is about to be replaced). */
      '#Cart-Drawer.sb-adding .product-cart-item{pointer-events:none}',
      /* Removing a piece: fade it straight away instead of nothing happening. */
      '.cart-drawer .product-cart-item.thb-loading{opacity:.4;pointer-events:none;transition:opacity .15s ease}',
      /* Checkout button: instant "on its way" state. */
      '.checkout-button.sb-going{cursor:progress;opacity:.92}',
      '.checkout-button .sb-spin{display:inline-block;width:14px;height:14px;margin-right:8px;vertical-align:-2px;',
      'border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:sbSpin .7s linear infinite}',
      '@keyframes sbSpin{to{transform:rotate(360deg)}}',
      '@media (prefers-reduced-motion: reduce){',
      '#Cart-Drawer.sb-adding .side-panel-header::after{animation:none;background-size:100% 100%}',
      '.cart-drawer .sb-pending-item{animation:none}',
      '.cart-drawer .sb-skel{animation:none}',
      '.checkout-button .sb-spin{animation:none;border-right-color:currentColor;opacity:.6}}'
    ].join('');
    var style = el('style');
    style.id = 'sb-fast-cart-css';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* ---------- pending (optimistic) state ---------- */

  function pendingItem(info) {
    var item = el('div', 'product-cart-item sb-pending-item');
    item.setAttribute('aria-busy', 'true');

    var imageWrap = el('div', 'product-cart-item-image');
    if (info.image) {
      var img = document.createElement('img');
      img.src = info.image;
      img.alt = '';
      img.decoding = 'async';
      imageWrap.appendChild(img);
    } else {
      imageWrap.appendChild(el('span', 'sb-pending-ph'));
    }

    var details = el('div', 'product-cart-item-info');
    if (info.vendor) details.appendChild(el('span', 'sb-brand', info.vendor));
    var title = el('a', 'cart-product-link', info.title || '');
    if (info.url) title.href = info.url;
    details.appendChild(title);
    if (info.price) {
      var price = el('span', 'price');
      var ins = el('ins');
      ins.appendChild(el('span', 'amount', info.price));
      price.appendChild(ins);
      details.appendChild(price);
    }
    details.appendChild(el('span', 'sb-pending-note', 'Adding to your bag…'));

    item.appendChild(imageWrap);
    item.appendChild(details);
    return item;
  }

  /* ---------- the rest of the drawer, drawn before Shopify answers ---------- */

  /* Same output as Liquid's | money for the shop's format (EGP {{amount_no_decimals}}). */
  function formatMoney(cents) {
    var format = (window.theme && theme.settings && theme.settings.money_with_currency_format) || '{{amount}}';
    function delimit(value, decimals, thousands, decimal) {
      var parts = (value / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[0] + (parts[1] ? decimal + parts[1] : '');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return delimit(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(cents, 2, "'", '.');
        default: return delimit(cents, 2, ',', '.');
      }
    });
  }

  function skeleton(cls) {
    var node = el('span', 'sb-skel' + (cls ? ' ' + cls : ''));
    node.innerHTML = '&nbsp;';
    return node;
  }

  /* Mirrors snippets/sb-cart-status.liquid: 10% off at 2 pieces, 15% at 3+.
     `original` is the bag's undiscounted subtotal in cents (NaN if unknown). */
  function fillTiers(box, count, original) {
    box.innerHTML = '';
    var lead = el('p', 'sb-tiers__lead');
    var known = !isNaN(original);
    var width = '100%';
    var ticks = null;
    if (count === 1) {
      if (known) lead.textContent = 'Add one more piece and ' + formatMoney(Math.floor(original * 10 / 100)) + ' comes off what\'s in your bag.';
      width = '33%';
      ticks = ['10% off at 2 pieces', '15% off at 3'];
    } else if (count === 2) {
      if (known) lead.textContent = 'Add one more piece and another ' + formatMoney(Math.floor(original * 5 / 100)) + ' comes off what\'s in your bag.';
      width = '66%';
      ticks = ['10% off is on', '15% off at 3'];
    } else {
      lead.textContent = '15% off is applied to your whole order.';
    }
    if (!lead.textContent) lead.appendChild(skeleton());
    box.appendChild(lead);

    var bar = el('div', 'sb-tiers__bar');
    var fill = el('span');
    fill.style.width = width;
    bar.appendChild(fill);
    box.appendChild(bar);

    if (ticks) {
      var row = el('div', 'sb-tiers__ticks');
      ticks.forEach(function (t) { row.appendChild(el('span', null, t)); });
      box.appendChild(row);
    }
  }

  function drawRestOfDrawer(d, info) {
    var inner = d.querySelector('.side-panel-inner');
    if (!inner || inner.dataset.sbCount == null) return;

    var count = (parseInt(inner.dataset.sbCount, 10) || 0) + 1;
    var price = info ? parseInt(info.priceCents, 10) : NaN;
    var original = (parseInt(inner.dataset.sbOriginal, 10) || 0) + price;

    /* "Cart · 2 pieces" */
    var heading = d.querySelector('.side-panel-header .h6');
    if (heading) {
      var counter = heading.querySelector('.sb-cart-count');
      if (!counter) {
        heading.appendChild(document.createTextNode(' '));
        counter = heading.appendChild(el('span', 'sb-cart-count'));
      }
      counter.textContent = count + (count === 1 ? ' piece' : ' pieces');
    }

    /* Footer: reuse the one on the page, or build it from the template
       when this is the first piece in the bag. */
    var footer = inner.querySelector('.side-panel-footer');
    if (!footer) {
      var tpl = inner.querySelector('#sb-footer-tpl');
      if (!tpl || !tpl.content) return;
      inner.appendChild(tpl.content.cloneNode(true));
      footer = inner.querySelector('.side-panel-footer');
      if (!footer) return;
    }

    var tiers = footer.querySelector('.sb-tiers');
    if (tiers) fillTiers(tiers, count, original);

    /* Amounts that depend on Shopify's discount calculation shimmer. */
    var total = footer.querySelector('.sb-co-total');
    if (total) {
      total.classList.add('sb-skel');
      total.innerHTML = '&nbsp;';
    }
    var saved = footer.querySelector('.sb-cart-saved');
    if (!saved && count >= 2) {
      var buttons = footer.querySelector('.cart-drawer-buttons');
      if (buttons) saved = buttons.parentNode.insertBefore(el('p', 'sb-cart-saved'), buttons);
    }
    if (saved) {
      saved.classList.add('sb-skel');
      saved.innerHTML = '&nbsp;';
    }
  }

  function clearPending(d) {
    if (!d) return;
    d.querySelectorAll('.sb-pending-item, .sb-pending-list').forEach(function (node) {
      node.remove();
    });
    var empty = d.querySelector('.cart-drawer__empty-cart');
    if (empty) empty.style.display = '';
  }

  function start(info) {
    var d = drawer();
    if (!d || typeof d.open !== 'function') return false;
    if (d.classList.contains('sb-adding')) return false;

    clearPending(d);
    /* Kept so a failed add can put the drawer back exactly as it was. */
    restoreHTML = d.innerHTML;
    d.classList.add('sb-adding');

    if (info && (info.title || info.image)) {
      var content = d.querySelector('.side-panel-content');
      if (content) {
        var empty = content.querySelector('.cart-drawer__empty-cart');
        if (empty) empty.style.display = 'none';
        var list = content.querySelector('.product-cart-item--container');
        if (!list) {
          list = el('div', 'product-cart-item--container sb-pending-list');
          content.insertBefore(list, content.firstChild);
        }
        /* Shopify lists the newest line first, so the pending piece goes on top. */
        list.insertBefore(pendingItem(info), list.querySelector('.product-cart-item'));
      }
    }

    try {
      drawRestOfDrawer(d, info);
    } catch (e) {
      /* Never let the preview get in the way of the real add. */
    }

    var search = document.getElementById('Search-Drawer');
    if (overlayIsOpen(search)) OverlayManager.close(search);

    d.open();

    clearTimeout(safetyTimer);
    safetyTimer = setTimeout(function () {
      if (d.classList.contains('sb-adding')) {
        finish();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }, SAFETY_MS);
    return true;
  }

  function finish() {
    clearTimeout(safetyTimer);
    restoreHTML = null;
    var d = drawer();
    if (!d) return;
    d.classList.remove('sb-adding');
    clearPending(d);
  }

  function fail() {
    var d = drawer();
    if (!d) return;
    var wasAdding = d.classList.contains('sb-adding');
    if (wasAdding && restoreHTML != null) d.innerHTML = restoreHTML;
    finish();
    if (wasAdding && overlayIsOpen(d)) OverlayManager.close(d);
  }

  /* If the request ends without the theme rendering the cart (network error,
     silent quick-add failure), close the optimistic drawer and resync it. */
  function watchButton(btn) {
    if (!btn || !window.MutationObserver) return;
    var observer = new MutationObserver(function () {
      if (btn.classList.contains('loading')) return;
      observer.disconnect();
      var d = drawer();
      if (d && d.classList.contains('sb-adding')) {
        fail();
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    });
    observer.observe(btn, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------- shared render (replaces the theme's renderContents) ---------- */

  function renderSections(ctx, parsedState) {
    var sections = (parsedState && parsedState.sections) || {};
    ctx.getSectionsToRender().forEach(function (s) {
      var host = document.getElementById(s.id);
      if (!host || !sections[s.section]) return;
      var target = host.querySelector(s.selector) || host;
      target.innerHTML = ctx.getSectionInnerHTML(sections[s.section], s.selector);
    });

    /* Cart page: re-bind the terms checkbox on the freshly rendered form
       (what the theme used to get by creating new Cart() objects). */
    var cartPage = document.getElementById('Cart');
    if (cartPage && typeof Cart !== 'undefined' && Cart.prototype.termsCheckbox) {
      Cart.prototype.termsCheckbox.call({ container: cartPage });
    }
  }

  function onlyExisting(list) {
    return list.filter(function (s) {
      return document.getElementById(s.id) !== null;
    });
  }

  /* ---------- product page form ---------- */

  function patchProductForm() {
    var PF = customElements.get('product-form');
    if (!PF || PF.prototype.__sbFast) return;
    PF.prototype.__sbFast = true;

    var baseSections = PF.prototype.getSectionsToRender;
    PF.prototype.getSectionsToRender = function () {
      return onlyExisting(baseSections.call(this));
    };

    PF.prototype.renderContents = function (parsedState) {
      renderSections(this, parsedState);

      var productDrawer = document.getElementById('Product-Drawer');
      var search = document.getElementById('Search-Drawer');
      if (overlayIsOpen(search)) OverlayManager.close(search);
      if (productDrawer && productDrawer.contains(this)) OverlayManager.close(productDrawer);

      finish();
      var d = drawer();
      if (d) d.open();
    };

    var baseError = PF.prototype.handleErrorMessage;
    PF.prototype.handleErrorMessage = function (message) {
      if (message) fail();
      return baseError.apply(this, arguments);
    };
  }

  function infoFromProductForm(pf) {
    var ds = pf.dataset;
    return {
      title: ds.sbTitle || '',
      vendor: ds.sbVendor || '',
      price: ds.sbPrice || '',
      priceCents: ds.sbPriceCents,
      image: ds.sbImage || '',
      url: ds.sbUrl || ''
    };
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches) return;
    if (!form.matches('form[data-type="add-to-cart-form"], form[action*="/cart/add"]')) return;
    /* Quick view keeps the theme's original flow (its drawer swaps with the cart drawer). */
    if (form.closest('#Product-Drawer')) return;
    if (!form.checkValidity()) return;

    var pf = form.closest('product-form');
    if (!pf || pf.dataset.sticky) return;

    var btn = form.querySelector('.single-add-to-cart-button');
    if (btn && (btn.disabled || btn.classList.contains('loading'))) return;

    if (start(infoFromProductForm(pf))) watchButton(btn);
  }, true);

  /* ---------- quick add on product cards ---------- */

  function patchProductCard() {
    var PC = customElements.get('product-card');
    if (!PC || PC.prototype.__sbFast) return;
    PC.prototype.__sbFast = true;

    var baseSections = PC.prototype.getSectionsToRender;
    PC.prototype.getSectionsToRender = function () {
      return onlyExisting(baseSections.call(this));
    };

    PC.prototype.renderContents = function (parsedState) {
      renderSections(this, parsedState);
      var productDrawer = document.getElementById('Product-Drawer');
      if (overlayIsOpen(productDrawer)) OverlayManager.close(productDrawer);
      finish();
      var d = drawer();
      if (d) d.open();
    };
  }

  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('.product-card--add-to-cart-button-simple');
    if (!btn || btn.disabled || btn.classList.contains('loading')) return;
    var card = btn.closest('product-card');
    if (!card) return;
    var img = card.querySelector('.product-primary-image');
    var info = {
      title: textOf(card.querySelector('.product-card-title')),
      vendor: textOf(card.querySelector('.product-card-vendor')),
      price: textOf(card.querySelector('.price ins .amount')) || textOf(card.querySelector('.price .amount')),
      image: img ? (img.currentSrc || img.src || '') : '',
      url: (card.querySelector('a[href*="/products/"]') || {}).href || ''
    };
    if (start(info)) watchButton(btn);
  }, true);

  /* ---------- removing a piece in the drawer ---------- */

  /* Same request as the theme's version, plus: only the sections on the page
     are rendered, and a failed request resyncs the drawer instead of leaving
     the piece stuck half-faded. */
  function patchCartDrawer() {
    var CD = customElements.get('cart-drawer');
    if (!CD || CD.prototype.__sbFast) return;
    CD.prototype.__sbFast = true;

    CD.prototype.updateQuantity = function (line, quantity) {
      var self = this;
      var row = this.querySelector('#CartDrawerItem-' + line);
      if (row) row.classList.add('thb-loading');

      var sections = onlyExisting(this.getSectionsToRender());
      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('line-item:change:start', { quantity: quantity });
      }

      fetch(theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          sections: sections.map(function (s) { return s.section; }),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) { return response.json(); })
        .then(function (state) {
          if (state && state.sections) {
            sections.forEach(function (s) {
              var host = document.getElementById(s.id);
              if (!host || !state.sections[s.section]) return;
              var target = host.querySelector(s.selector) || host;
              target.innerHTML = self.getSectionInnerHTML(state.sections[s.section], s.selector);
            });
          } else {
            self.refresh();
          }
          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('line-item:change:end', { quantity: quantity, cart: state });
          }
        })
        .catch(function () {
          self.refresh();
        })
        .finally(function () {
          var stale = self.querySelector('#CartDrawerItem-' + line);
          if (stale) stale.classList.remove('thb-loading');
        });
    };
  }

  /* ---------- checkout button ---------- */

  /* The checkout button is a normal form post to /cart, which Shopify answers
     with a redirect to checkout. That navigation is left exactly as it is (so
     anything an app hooks into it keeps working); the button just reacts the
     instant it's tapped instead of sitting still until checkout has loaded,
     and a second tap no longer sends a second request. */
  var CHECKOUT_RESET_MS = 12000;

  function isCheckoutButton(btn) {
    return !!(btn && btn.name === 'checkout' && btn.classList && btn.classList.contains('checkout-button'));
  }

  function markGoing(btn) {
    if (btn.classList.contains('sb-going')) return;
    btn.classList.add('sb-going');
    btn.setAttribute('aria-busy', 'true');
    var label = btn.querySelector('span:not(.sb-co-total)');
    if (label) {
      label.dataset.sbLabel = label.textContent;
      label.textContent = 'Opening checkout';
      label.insertBefore(el('span', 'sb-spin'), label.firstChild);
    }
    btn.__sbReset = setTimeout(function () { resetGoing(btn); }, CHECKOUT_RESET_MS);
  }

  function resetGoing(btn) {
    clearTimeout(btn.__sbReset);
    btn.classList.remove('sb-going');
    btn.removeAttribute('aria-busy');
    var label = btn.querySelector('span[data-sb-label]');
    if (label) {
      label.textContent = label.dataset.sbLabel;
      delete label.dataset.sbLabel;
    }
  }

  document.addEventListener('submit', function (event) {
    var btn = event.submitter || document.activeElement;
    if (!isCheckoutButton(btn)) return;
    if (btn.classList.contains('sb-going')) {
      event.preventDefault();
      return;
    }
    /* Let every other handler run first; only show the state if the
       navigation is really going ahead. */
    setTimeout(function () {
      if (!event.defaultPrevented) markGoing(btn);
    }, 0);
  }, true);

  /* Coming back from checkout with the browser's Back button restores the page
     from memory; put the button back to normal. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll('.checkout-button.sb-going').forEach(resetGoing);
    var d = drawer();
    if (d && d.classList.contains('sb-adding')) finish();
    /* The cart may have changed in checkout; reload the drawer's contents. */
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  });

  /* ---------- background pollers ---------- */

  function quietPollers() {
    /* Bundle & save box (snippets/sb-bundle-tiers): exposes stop() for its 4s timer. */
    if (window.__sbBundle && typeof window.__sbBundle.stop === 'function') {
      window.__sbBundle.stop();
    }

    /* Custom header block (blocks/ai_gen_block_307bd65): 5s cart-count timer. */
    document.querySelectorAll('[class*="ai-custom-header-"]').forEach(function (header) {
      if (!header.updateInterval) return;
      clearInterval(header.updateInterval);
      header.updateInterval = null;
      if (header.__sbVisibility || typeof header.fetchCartCount !== 'function') return;
      header.__sbVisibility = true;
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') header.fetchCartCount();
      });
      window.addEventListener('pageshow', function (e) {
        if (e.persisted) header.fetchCartCount();
      });
    });
  }

  /* ---------- boot ---------- */

  injectStyles();
  patchProductForm();
  patchProductCard();
  patchCartDrawer();
  quietPollers();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', quietPollers);
  }
  window.addEventListener('load', quietPollers);

  window.SBFastCart = { start: start, finish: finish, fail: fail };
})();
